import { useNavigate } from 'react-router-dom';
import AsyncButton from '@wojtekmaj/react-async-button';
import T, { useTranslation } from '@wojtekmaj/react-t';

import { toast } from '@rewardopl/react-toast';

import Button from '@rewardopl/react-ui/button';
import InputField from '@rewardopl/react-ui/input_field';

import { patch } from '@rewardopl/utils/network';

import { ButtonWrapper } from '../login/email/form.styles';

import type { User } from '@rewardopl/types';

const pendingConfig = {
  disabled: true,
};

type PasswordResetFormProps = {
  id: User['_id'];
  token: NonNullable<User['password_reset_token']>;
};

export default function PasswordResetForm({ id, token }: PasswordResetFormProps) {
  const navigate = useNavigate();
  const successString = useTranslation('Password reset successfully');
  const accountDidNotRequestPasswordResetString = useTranslation(
    'Password reset has not been requested or password reset link has already been used',
  );
  const tokenIsInvalidString = useTranslation(
    'Password reset link is invalid. Check if you copied the link correctly',
  );
  const tokenIsExpiredString = useTranslation(
    'Password reset link has expired. Request password reset again',
  );
  const passwordConfirmInvalidString = useTranslation(
    'New password and New password (confirm) do not match',
  );
  const genericErrorString = useTranslation('Failed to reset password');

  const action = `/api/accounts/${id}`;

  async function onClick(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();

    const submitButton = event.currentTarget;
    const form = submitButton.form;

    if (!form || !form.reportValidity()) {
      return;
    }

    try {
      await patch(action, {
        body: new FormData(form),
      });

      navigate('/login');

      toast.success(successString);
    } catch (err) {
      const error = err;

      const errorString = (() => {
        if (!(error instanceof Error)) {
          return genericErrorString;
        }

        switch (error.message) {
          case 'Account did not request password reset':
            return accountDidNotRequestPasswordResetString;
          case 'token is invalid':
            return tokenIsInvalidString;
          case 'token is expired':
            return tokenIsExpiredString;
          case 'passwords do not match':
            return passwordConfirmInvalidString;
          default:
            return genericErrorString;
        }
      })();

      toast.error(errorString);
    }
  }

  return (
    <form action={action} method="POST">
      <input name="token" type="hidden" value={token} />
      <InputField
        autoComplete="new-password"
        hint="Min. 8 characters, uppercase letter, lowercase letter, number, special character"
        label="New password"
        name="new_password"
        type="password"
      />
      <InputField
        autoComplete="new-password"
        label="New password (confirm)"
        name="new_password_confirm"
        type="password"
      />
      <ButtonWrapper>
        <AsyncButton
          as={Button}
          className="primary"
          onClick={onClick}
          pendingConfig={pendingConfig}
          type="submit"
        >
          <T>Save</T>
        </AsyncButton>
      </ButtonWrapper>
    </form>
  );
}
